import { FormEvent, useState } from "react";
import { useSnackbar } from "notistack";
import { Link, useNavigate } from "react-router-dom";
import { ApolloFiltersEditor } from "../components/ApolloFiltersEditor";
import { ExampleDomains } from "../components/ExampleDomains";
import { StatusLine } from "../components/StatusLine";
import { useStartAuthoringRun } from "../hooks/useAuthoring";
import {
  useAutofillKeywords,
  useAutofillRegions,
  useAutofillTitles,
  useCreateIcp,
} from "../hooks/useIcps";
import { useAddIcpSamplesFor } from "../hooks/useIcpSamples";
import type { ApolloFilters } from "../lib/types";

const NAME_PATTERN = /^[a-z][a-z0-9_]{0,63}$/;

function toSlug(displayName: string): string {
  return displayName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "")
    .slice(0, 64);
}

const DEFAULT_FILTERS: ApolloFilters = {
  titles: [
    "CEO",
    "Chief Executive Officer",
    "Founder",
    "Co-Founder",
    "President",
    "Managing Director",
    "CPO",
    "Chief Product Officer",
    "VP Product",
    "VP of Product",
    "Vice President of Product",
    "Head of Product",
    "CRO",
    "Chief Revenue Officer",
    "CTO",
    "Chief Technology Officer",
    "VP Engineering",
    "VP of Engineering",
    "VP Legal",
    "VP Compliance",
    "VP Legal & Compliance",
    "COO",
    "Chief Operating Officer",
  ],
  seniorities: ["c_suite", "founder", "owner", "vp"],
  min_employees: 50,
  max_employees: 800,
  min_revenue: 5_000_000,
  max_revenue: 110_000_000,
};

function mergeList(existing: string[], incoming: string[]): string[] {
  const out = [...existing];
  const seen = new Set(existing.map((r) => r.toLowerCase()));
  for (const r of incoming) {
    const v = r.trim();
    if (v && !seen.has(v.toLowerCase())) {
      seen.add(v.toLowerCase());
      out.push(v);
    }
  }
  return out;
}

export function AutomaticRun() {
  const { enqueueSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const create = useCreateIcp();
  const addSamples = useAddIcpSamplesFor();
  const startAuthoring = useStartAuthoringRun();
  const autofillRegions = useAutofillRegions();
  const autofillTitles = useAutofillTitles();
  const autofillKeywords = useAutofillKeywords();

  const [displayName, setDisplayName] = useState("");
  const [campaignId, setCampaignId] = useState("");
  const [context, setContext] = useState("");
  const [filters, setFilters] = useState<ApolloFilters>(DEFAULT_FILTERS);
  const [positiveDomains, setPositiveDomains] = useState<string[]>([]);
  const [negativeDomains, setNegativeDomains] = useState<string[]>([]);
  const [negativeNotes, setNegativeNotes] = useState<Record<string, string>>({});
  const [autofilling, setAutofilling] = useState(false);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  const [created, setCreated] = useState<{
    name: string;
    displayName: string;
    hasCampaign: boolean;
    authoringStarted: boolean;
  } | null>(null);

  const onAutofill = async () => {
    const ctx = context.trim();
    if (!ctx && positiveDomains.length === 0) {
      enqueueSnackbar("Add the context (and/or some example companies) first.", {
        variant: "warning",
      });
      return;
    }
    setAutofilling(true);
    try {
      const [regions, titles, keywords] = await Promise.all([
        ctx ? autofillRegions.mutateAsync(ctx) : Promise.resolve<string[]>([]),
        ctx ? autofillTitles.mutateAsync(ctx) : Promise.resolve<string[]>([]),
        positiveDomains.length > 0
          ? autofillKeywords.mutateAsync({ context: ctx, companies: positiveDomains })
          : Promise.resolve<string[]>([]),
      ]);
      setFilters((prev) => ({
        ...prev,
        titles: mergeList(prev.titles ?? [], titles),
        regions: mergeList(prev.regions ?? [], regions),
        organization_keywords: mergeList(prev.organization_keywords ?? [], keywords),
      }));
      setFiltersOpen(true);
      enqueueSnackbar(
        `Filled ${titles.length} title(s), ${regions.length} region(s), ${keywords.length} keyword(s).`,
        { variant: "success" },
      );
    } catch (err) {
      enqueueSnackbar(err instanceof Error ? err.message : String(err), {
        variant: "error",
      });
    } finally {
      setAutofilling(false);
    }
  };

  const onSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);

    const trimmedDisplay = displayName.trim();
    if (!trimmedDisplay) {
      setError("Display name is required.");
      return;
    }
    const slug = toSlug(trimmedDisplay);
    if (!NAME_PATTERN.test(slug)) {
      setError(
        "Display name must start with a letter so it can be turned into an internal ID.",
      );
      return;
    }
    const trimmedContext = context.trim();
    if (!trimmedContext) {
      setError("Context is required — this is the AI-facing intent.");
      return;
    }
    const trimmedCampaign = campaignId.trim();
    if (trimmedCampaign && !trimmedCampaign.startsWith("cam_")) {
      setError("Lemlist campaign ID must start with cam_ (or leave it blank).");
      return;
    }

    setSubmitting(true);
    try {
      let icpName: string;
      try {
        const icp = await create.mutateAsync({
          name: slug,
          display_name: trimmedDisplay,
          context: trimmedContext,
          lemlist_campaign_id: trimmedCampaign || null,
          search_filters: filters,
          is_active: false,
        });
        icpName = icp.name;
      } catch (err) {
        setError(err instanceof Error ? err.message : String(err));
        return;
      }

      const samples = [
        ...positiveDomains.map((domain) => ({ label: "positive" as const, domain })),
        ...negativeDomains.map((domain) => {
          const reason = negativeNotes[domain]?.trim();
          return {
            label: "negative" as const,
            domain,
            ...(reason ? { notes: reason } : {}),
          };
        }),
      ];
      if (samples.length > 0) {
        try {
          await addSamples.mutateAsync({ icp_name: icpName, samples });
        } catch (err) {
          enqueueSnackbar(
            `ICP created, but examples failed to save: ${
              err instanceof Error ? err.message : err
            }`,
            { variant: "warning" },
          );
        }
      }

      let authoringStarted = false;
      if (positiveDomains.length > 0 && negativeDomains.length > 0) {
        try {
          await startAuthoring.mutateAsync({
            icp_name: icpName,
            mode: "improve",
            use_stored_samples: true,
            max_iterations: 1,
            auto_promote: true,
          });
          authoringStarted = true;
          enqueueSnackbar("Generating a v1 scoring prompt…", { variant: "info" });
        } catch (err) {
          enqueueSnackbar(
            `Prompt authoring didn't start: ${
              err instanceof Error ? err.message : err
            }. You can run it on the Improve prompt page.`,
            { variant: "warning" },
          );
        }
      }

      enqueueSnackbar(`Created ${trimmedDisplay}.`, { variant: "success" });
      setCreated({
        name: icpName,
        displayName: trimmedDisplay,
        hasCampaign: Boolean(trimmedCampaign),
        authoringStarted,
      });
    } finally {
      setSubmitting(false);
    }
  };

  if (created) {
    return (
      <section className="card">
        <h2>ICP created — {created.displayName}</h2>
        {created.authoringStarted ? (
          <>
            <p className="card-desc">
              A v1 scoring prompt is being drafted in the background. When it's
              ready it's promoted automatically and the ICP goes{" "}
              <strong>active</strong> — no manual step.
            </p>
            <ol className="card-desc" style={{ paddingLeft: 18 }}>
              {created.hasCampaign ? (
                <li>
                  Campaign id is set, so once the prompt lands it's researched
                  automatically every weekday. Review the generated prompt on
                  the ICP page when you get a chance.
                </li>
              ) : (
                <li>
                  No campaign yet: create one in Lemlist, then paste its id
                  (<code>cam_…</code>) on the ICP settings page. Until then the
                  daily run skips this ICP even though it's active.
                </li>
              )}
            </ol>
          </>
        ) : (
          <>
            <p className="card-desc">
              Created and <strong>inactive</strong> with no prompt yet — you
              didn't provide both positive and negative examples, so no prompt
              was drafted. To switch it on:
            </p>
            <ol className="card-desc" style={{ paddingLeft: 18 }}>
              <li>
                On the ICP page, add example companies and run{" "}
                <strong>Improve prompt</strong> to generate a scoring prompt.
              </li>
              {!created.hasCampaign && (
                <li>
                  Create a Lemlist campaign and paste its id (<code>cam_…</code>)
                  on the ICP settings page.
                </li>
              )}
              <li>
                Promote the prompt and toggle <strong>Active</strong> — then
                it's researched automatically every weekday.
              </li>
            </ol>
          </>
        )}
        <div className="field" style={{ maxWidth: 360 }}>
          <label>ICP id</label>
          <div style={{ display: "flex", gap: 8 }}>
            <input type="text" value={created.name} readOnly />
            <button
              type="button"
              onClick={() => {
                void navigator.clipboard?.writeText(created.name);
                enqueueSnackbar("Copied.", { variant: "success" });
              }}
            >
              Copy
            </button>
          </div>
        </div>
        <div style={{ display: "flex", gap: 8, marginTop: 16 }}>
          <button type="button" onClick={() => navigate(`/icps/${created.name}`)}>
            Go to ICP settings →
          </button>
          <Link to="/icps" className="button-link">
            All ICPs
          </Link>
        </div>
      </section>
    );
  }

  return (
    <section className="card">
      <h2>Automatic run</h2>
      <p className="card-desc">Create a new ICP to research automatically.</p>
      <form onSubmit={onSubmit}>
        <div className="field-row" style={{ alignItems: "flex-start" }}>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="ar-display">Display name</label>
            <input
              id="ar-display"
              type="text"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="ConTech / Trade SaaS"
              required
              autoFocus
            />
            <p className="card-desc" style={{ fontSize: 11 }}>
              Internal ID: <code>{toSlug(displayName) || "—"}</code>
            </p>
          </div>
          <div className="field" style={{ flex: 1 }}>
            <label htmlFor="ar-campaign">Lemlist campaign ID (optional)</label>
            <input
              id="ar-campaign"
              type="text"
              value={campaignId}
              onChange={(e) => setCampaignId(e.target.value)}
              placeholder="cam_…"
            />
            <p className="card-desc" style={{ fontSize: 11 }}>
              From Lemlist (you create the campaign there). Leave blank to add
              later on the ICP settings page.
            </p>
          </div>
        </div>

        <div className="field">
          <label htmlFor="ar-context">Context (AI-facing)</label>
          <textarea
            id="ar-context"
            value={context}
            onChange={(e) => setContext(e.target.value)}
            rows={6}
            placeholder="Who does this ICP target? Segment, size band, geography, key signals."
            required
            style={{ resize: "vertical" }}
          />
        </div>

        <div className="field-row" style={{ alignItems: "flex-start" }}>
          <ExampleDomains
            label="Positive examples"
            color="var(--success)"
            values={positiveDomains}
            onChange={setPositiveDomains}
          />
          <ExampleDomains
            label="Negative examples"
            color="var(--error)"
            values={negativeDomains}
            onChange={setNegativeDomains}
            notes={negativeNotes}
            onNotesChange={setNegativeNotes}
          />
        </div>

        <div className="field">
          <label>Search filters</label>
          <p className="card-desc" style={{ fontSize: 11 }}>
            Pre-filled with defaults. Auto-fill regions & keywords from the
            context and example companies, then review only if needed.
          </p>
          <button
            type="button"
            onClick={onAutofill}
            disabled={autofilling || (!context.trim() && positiveDomains.length === 0)}
            style={{ marginBottom: 8 }}
          >
            {autofilling ? "Autofilling…" : "Auto-fill regions & keywords"}
          </button>
          <details
            open={filtersOpen}
            onToggle={(e) =>
              setFiltersOpen((e.currentTarget as HTMLDetailsElement).open)
            }
          >
            <summary style={{ cursor: "pointer", fontSize: 13 }}>
              Review filters
            </summary>
            <div style={{ marginTop: 8 }}>
              <ApolloFiltersEditor value={filters} onChange={setFilters} />
            </div>
          </details>
        </div>

        {error && <StatusLine variant="error" message={error} />}

        <button type="submit" disabled={submitting} style={{ marginTop: 16 }}>
          {submitting ? "Creating…" : "Create ICP"}
        </button>
      </form>
    </section>
  );
}
